/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================
*/

import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import HelpTooltip from "components/HelpTooltip";

function SectionHeader({ title, subtitle, action, helpText, variant, align, sx }) {
  const isCompact = variant === "compact";
  const isCentered = align === "center";
  const hasTrailingContent = Boolean(action || helpText);

  return (
    <MDBox
      sx={[
        (theme) => ({
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: hasTrailingContent
            ? "space-between"
            : isCentered
            ? "center"
            : "flex-start",
          flexWrap: "wrap",
          gap: 1.5,
          minHeight: isCompact ? 40 : 52,
          px: isCompact ? 1.5 : variant === "card" ? 2.5 : 2,
          py: isCompact ? 0.75 : variant === "card" ? 1.75 : 1.5,
          textAlign: align,
          backgroundColor: alpha(theme.palette.info.main, isCompact ? 0.04 : 0.06),
          borderBottom: "1px solid " + alpha(theme.palette.info.main, 0.24),
          borderLeft: isCompact || isCentered ? "none" : "4px solid " + theme.palette.info.main,
          borderRadius: theme.shape.borderRadius,
        }),
        sx,
      ]}
    >
      <MDBox minWidth={0} flex={hasTrailingContent ? "1 1 12rem" : "0 1 auto"}>
        <MDTypography variant={isCompact ? "h6" : "h5"} fontWeight="medium" lineHeight={1.3}>
          {title}
        </MDTypography>
        {subtitle && (
          <MDTypography variant="button" color="text" fontWeight="regular" opacity={0.8}>
            {subtitle}
          </MDTypography>
        )}
      </MDBox>
      {hasTrailingContent && (
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}
          width={action ? { xs: "100%", md: "auto" } : "auto"}
          flex="0 0 auto"
        >
          {action}
          {helpText && <HelpTooltip title={helpText} />}
        </MDBox>
      )}
    </MDBox>
  );
}

SectionHeader.defaultProps = {
  subtitle: null,
  action: null,
  helpText: "",
  variant: "page",
  align: "left",
  sx: null,
};

SectionHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  helpText: PropTypes.string,
  variant: PropTypes.oneOf(["page", "card", "compact"]),
  align: PropTypes.oneOf(["left", "center"]),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

export default SectionHeader;
